import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { getReferral } from "@/lib/referral";

const AuthContext = createContext(null);

const TOKEN_KEY = "abeille-admin-token";
const REFRESH_KEY = "abeille-admin-refresh";

const storeTokens = (data) => {
  if (data?.access_token) {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(REFRESH_KEY, data.refresh_token);
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = vérification en cours, false = déconnecté

  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return;
      } catch {
        // on tente un renouvellement avec le jeton de secours
      }
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (refreshToken) {
        try {
          const { data } = await api.post("/auth/refresh", {}, { headers: { Authorization: `Bearer ${refreshToken}` } });
          storeTokens(data);
          const me = await api.get("/auth/me");
          setUser(me.data);
          return;
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_KEY);
        }
      }
      setUser(false);
    };
    init();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.requires_2fa) return data; // étape 2FA : pas de session tant que le code n'est pas validé
    storeTokens(data);
    setUser(data);
    refreshUser();
    return data;
  };

  const verifyTwoFactor = async (challenge, code) => {
    const { data } = await api.post("/auth/login/2fa", { challenge, code });
    storeTokens(data);
    setUser(data);
    refreshUser();
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    storeTokens(data);
    setUser(data);
    const ref = getReferral();
    if (ref?.code) await api.post("/customer/referral/claim", { code: ref.code }).catch(() => {});
    refreshUser();
    return data;
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // silencieux : l'utilisateur reste tel quel
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // on déconnecte localement quoi qu'il arrive
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setUser(false);
  };

  const value = useMemo(() => ({ user, login, verifyTwoFactor, register, logout, refreshUser }), [user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
